import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
    const { currentUser, hasTokenClaim } = useAuth();
    const location = useLocation();
    const { t } = useTranslation();
    const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);

    useEffect(() => {
        // Add a small delay to avoid flashing of content
        const timeout = setTimeout(() => {
            setIsVerifyingAuth(false);
        }, 300); // Reduced delay for better user experience

        return () => clearTimeout(timeout);
    }, []);

    // Show loading state while auth is being checked
    if (isVerifyingAuth) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100vh',
                }}
                role="status"
                aria-label={t('auth.privateRoute.verifyingAuth')}
            >
                <CircularProgress color="primary" size={40} thickness={4} />
            </Box>
        );
    }

    // Auth check failed - redirect to login
    if (!currentUser) {
        // Store the attempted URL for later redirection after login
        sessionStorage.setItem('redirectAfterLogin', location.pathname);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If user hasn't finished onboarding and not already on the onboarding page, redirect to onboarding
    // Allow both verified and unverified users to access onboarding
    if (
        currentUser &&
        !hasTokenClaim('onboarding_finished') &&
        location.pathname !== '/onboarding' &&
        !location.pathname.includes('/plans/payment')
    ) {
        return <Navigate to="/onboarding" replace />;
    }

    // Email verification is no longer required for any routes
    // Users can access the entire app without email verification

    // All checks passed, render the protected content
    return children;
};

export default PrivateRoute;
